import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { useUpdateVideoProgressMutation } from '../../slices/credentialingApi';
import './VideoProgressTracker.css';
import util from '../../styles/utilities.module.css';

const VideoProgressTracker = ({ 
  videoUrl, 
  moduleId, 
  completionThreshold = 90,
  onModuleCompleted,
  onVideoEnded,
  initialProgress = 0,
  initialPosition = 0,
  isModuleCompleted = false,
  hideProgressUI = false
}) => {
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [progress, setProgress] = useState(initialProgress);
  const [currentTime, setCurrentTime] = useState(initialPosition);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  
  const playerRef = useRef(null);
  const lastProgressUpdate = useRef(0);
  const progressUpdateTimeout = useRef(null);
  const latestProgressRef = useRef({ currentTime: initialPosition, duration: 0 });
  const updateProgressRef = useRef(null);
  const [updateVideoProgress] = useUpdateVideoProgressMutation();
  const hasTriggeredEndRef = useRef(false);

  // Update progress to backend with throttling (every 5 seconds)
  const updateProgressToBackend = useCallback(async (currentTime, duration, eventType = 'progress') => {
    try {
      const result = await updateVideoProgress({
        moduleId: parseInt(moduleId),
        currentTime: Math.floor(currentTime),
        duration: Math.floor(duration),
        eventType
      }).unwrap();

      // Check if module was completed
      if (result.data?.module_completed && !showCompletionMessage) {
        setShowCompletionMessage(true);
        if (onModuleCompleted) {
          onModuleCompleted('video', result.data);
        }
      }
    } catch (error) {
      console.error('Failed to update video progress:', error);
    }
  }, [moduleId, updateVideoProgress, onModuleCompleted, showCompletionMessage]);

  // Throttled progress update function
  const throttledProgressUpdate = useCallback((currentTime, duration) => {
    const now = Date.now();
    if (now - lastProgressUpdate.current > 5000) { // Update every 5 seconds
      lastProgressUpdate.current = now;
      updateProgressToBackend(currentTime, duration, 'progress');
    }
  }, [updateProgressToBackend]);

  // Handle video progress updates
  const handleProgress = ({ played, playedSeconds }) => {
    const newProgress = played * 100;
    // Use the maximum of current session progress and initial backend progress
    const maxProgress = Math.max(newProgress, initialProgress);
    setProgress(maxProgress);
    setCurrentTime(playedSeconds);
    console.log('Video progress:', maxProgress);
    // If user effectively reached 100% (accounts for floating point), trigger end callback once
    if (!hasTriggeredEndRef.current && maxProgress >= 99.9) {
      hasTriggeredEndRef.current = true;
      if (typeof onVideoEnded === 'function') {
        onVideoEnded();
      }
    }
    
    // Throttled backend update
    if (duration > 0) {
      throttledProgressUpdate(playedSeconds, duration);
    }
  };

  // Handle video duration change
  const handleDuration = (videoDuration) => {
    setDuration(videoDuration);
    if (initialPosition > 0 && playerRef.current) {
      // Seek to initial position if available
      playerRef.current.seekTo(initialPosition, 'seconds');
    }
  };

  useEffect(() => {
    latestProgressRef.current = { currentTime, duration };
  }, [currentTime, duration]);

  useEffect(() => {
    updateProgressRef.current = updateProgressToBackend;
  }, [updateProgressToBackend]);

  // Handle video end
  const handleEnded = () => {
    console.log('Video ended');
    setIsPlaying(false);
    updateProgressToBackend(duration, duration, 'ended');
    if (typeof onVideoEnded === 'function') {
      onVideoEnded();
    }
  };

  // Handle play/pause
  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
    // Update progress immediately on pause
    if (duration > 0) {
      updateProgressToBackend(currentTime, duration, 'pause');
    }
  };

  // Handle seeking
  const handleSeek = (seconds) => {
    // Clear pending throttled updates when user seeks
    if (progressUpdateTimeout.current) {
      clearTimeout(progressUpdateTimeout.current);
    }
    
    // Update backend immediately on seek
    updateProgressToBackend(seconds, duration, 'seeked');
  };

  // Handle video ready
  const handleReady = () => {
    setVideoLoading(false);
    setVideoError(false);
  };

  // Handle video error
  const handleError = (error) => {
    console.error('Video playback error:', error);
    setVideoLoading(false);
    setVideoError(true);
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate completion percentage
  const completionPercentage = Math.min(progress, 100);
  const isNearCompletion = completionPercentage >= completionThreshold;

  // Keyboard accessibility: space/enter toggle, arrows seek
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only when focus is within the video container
      const active = document.activeElement;
      if (!active) return;
      const isInside = active.closest && active.closest('.video-container');
      if (!isInside) return;
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        setIsPlaying((prev) => !prev);
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        if (playerRef.current) {
          const target = Math.min(currentTime + 5, duration);
          playerRef.current.seekTo(target, 'seconds');
        }
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        if (playerRef.current) {
          const target = Math.max(currentTime - 5, 0);
          playerRef.current.seekTo(target, 'seconds');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTime, duration]);

  // Flush latest progress when component unmounts (e.g., modal closed quickly)
  useEffect(() => () => {
    const { currentTime: latestTime, duration: latestDuration } = latestProgressRef.current;
    if (latestDuration > 0 && latestTime > 0 && updateProgressRef.current) {
      updateProgressRef.current(latestTime, latestDuration, 'unmount');
    }
  }, []);

  return (
    <div className="video-progress-tracker" aria-label="Training video" role="group">
      {/* Video container */}
      <div className="video-container" tabIndex={0} aria-label="Video player container">
        {/* Loading state */}
        {videoLoading && !videoError && (
          <div className="video-loading-placeholder">
            <div className="video-skeleton">
              <div className="skeleton-play-button">
                <div className="play-icon">▶</div>
              </div>
              <div className="skeleton-controls">
                <div className="skeleton-progress-bar"></div>
                <div className="skeleton-buttons">
                  <div className="skeleton-button"></div>
                  <div className="skeleton-button"></div>
                  <div className="skeleton-button"></div>
                </div>
              </div>
            </div>
            <div className="loading-text">Loading video...</div>
          </div>
        )}

        {/* Error state */}
        {videoError && (
          <div className="video-error-placeholder">
            <div className="error-icon">⚠️</div>
            <p>Unable to load video</p>
            <button 
              className={`${util.btn} ${util['btn--danger']} ${util['btn-sm']}`}
              onClick={() => {
                setVideoError(false);
                setVideoLoading(true);
              }}
            >
              Try Again
            </button>
            {videoUrl && (
              <a href={videoUrl} target="_blank" rel="noreferrer" className="retry-button" aria-label="Open video in new tab">Open in new tab</a>
            )}
          </div>
        )}

        {/* Video player */}
        <div style={{ display: videoLoading || videoError ? 'none' : 'block' }}>
          <div className="responsive-player" aria-label="Responsive video wrapper">
          <ReactPlayer
            ref={playerRef}
            config={{
              vimeo: {
                playerOptions: {
                  controls: true,
                },
              },
            }}
            controls={true}
            url={videoUrl}
            playing={isPlaying}
            width="100%"
            height="100%"
            onReady={handleReady}
            onError={handleError}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onSeek={handleSeek}
            progressInterval={1000} // Update progress every second
          />
          </div>
        </div>
      </div>

      {/* Progress information */}
      {!hideProgressUI && !videoLoading && !videoError && duration > 0 && (
        <div className="video-progress-info">
          <div className="progress-stats">
            <div className="progress-bar-container">
              <div className="progress-label">
                <span>Progress: {completionPercentage.toFixed(1)}%</span>
                <span className="time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className={`progress-fill ${isNearCompletion ? 'near-completion' : ''}`}
                  style={{ width: `${completionPercentage}%` }}
                />
                <div 
                  className="completion-threshold-marker"
                  style={{ left: `${completionThreshold}%` }}
                  title={`${completionThreshold}% completion required`}
                />
              </div>
            </div>
            
            
            {!isModuleCompleted && isNearCompletion && (
              <div className="completion-status">
                <span className="completion-badge">
                  ✅ Module completion requirements met
                </span>
              </div>
            )}
            
            {!isModuleCompleted && !isNearCompletion && (
              <div className="completion-requirement">
                <span className="requirement-text">
                  Watch {completionThreshold}% to complete this module
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoProgressTracker;
